<template>
  <div class="tw:flex tw:flex-col tw:h-full">
    <EdroneNavigationTop type="drawer">
      <template #header>
        <div class="tw:flex-shrink-0 tw:w-full">
          <EdroneHeader :title="trans._freemium.assasForm.title" type="h2" />
        </div>
      </template>
    </EdroneNavigationTop>

    <div class="tw:flex-1 tw:overflow-y-auto tw:flex tw:flex-col tw:gap-4 tw:px-6">
      <div>
        <p class="tw:body-1">{{ trans._freemium.assasForm.customerType }}</p>
        <div class="tw:flex tw:gap-2">
          <EdroneRadioButton
            unique-id=""
            :value="CustomerType.CPF"
            :label="trans._freemium.assasForm.cpf"
            class="tw:flex-1"
            :checked="customerType === CustomerType.CPF"
            @change="handleCustomerTypeChange"
          />
          <EdroneRadioButton
            unique-id=""
            :value="CustomerType.CNPJ"
            :label="trans._freemium.assasForm.cnpj"
            class="tw:flex-1"
            :checked="customerType === CustomerType.CNPJ"
            @change="handleCustomerTypeChange"
          />
        </div>
      </div>

      <div class="tw:flex tw:gap-4">
        <EdroneInput
          :value="email"
          :label="trans._freemium.assasForm.email"
          placeholder="user@email.com"
          type="email"
          class="tw:!w-1/2"
          @input="email = $event"
        >
          <template #icon>
            <FontAwesomeIcon icon="fa-light fa-envelope" />
          </template>
        </EdroneInput>

        <div class="tw:flex tw:gap-2 tw:w-1/2 tw:items-end">
          <EdroneSelectCustom
            :allow-empty="false"
            :selected-options="phonePrefix"
            :options="['+55']"
            :label="trans._freemium.assasForm.phone"
            placeholder="+XX"
            @input="phonePrefix = $event"
          />
          <EdroneInput :value="phoneNumber" placeholder="00000-0000" type="number" @input="phoneNumber = $event">
            <template #icon>
              <FontAwesomeIcon icon="fa-light fa-phone" />
            </template>
          </EdroneInput>
        </div>
      </div>

      <EdroneInput
        :value="fullName"
        :label="
          customerType === CustomerType.CNPJ
            ? trans._freemium.assasForm.companyName
            : trans._freemium.assasForm.personName
        "
        placeholder="João da Silva"
        @input="fullName = $event"
      />

      <div class="tw:flex tw:gap-4">
        <EdroneInput
          v-if="customerType === CustomerType.CNPJ"
          :value="cnpj"
          label="CNPJ"
          placeholder="00.000.000/0000-00"
          @input="cnpj = $event"
        />

        <EdroneInput v-else label="CPF" placeholder="000.000.000-00" :value="cpf" @input="cpf = $event" />
        <EdroneInput
          :value="postalCode"
          :label="trans._freemium.assasForm.postalCode"
          placeholder="00000-000"
          @input="postalCode = $event"
        />
      </div>
      <hr />

      <EdroneInput
        v-if="customerType === CustomerType.CNPJ"
        :value="companyName"
        :label="trans._freemium.assasForm.company"
        @input="companyName = $event"
      />

      <div class="tw:flex tw:gap-4">
        <EdroneInput :value="state" :label="trans._freemium.assasForm.state" placeholder="UF" @input="state = $event" />
        <EdroneInput
          :value="street"
          :label="trans._freemium.assasForm.street"
          placeholder="Logradouro"
          @input="street = $event"
        />
      </div>

      <div class="tw:flex tw:gap-4">
        <EdroneInput
          :value="streetNumber"
          :label="trans._freemium.assasForm.number"
          placeholder="Número"
          @input="streetNumber = $event"
        />
        <EdroneInput
          :value="complement"
          :label="trans._freemium.assasForm.additionalInfo"
          placeholder="Complemento"
          @input="complement = $event"
        />
      </div>

      <template v-if="customerType === CustomerType.CNPJ">
        <hr />
        <div>
          <p class="tw:body-1 tw:mb-1">{{ trans._freemium.assasForm.tax }}</p>
          <div class="tw:flex tw:gap-2">
            <EdroneToggle
              unique-id=""
              :checked="simplesNacional"
              :value="simplesNacional"
              label="Simples Nacional"
              @click="
                simplesNacional = $event;
                simei = $event ? simei : false;
              "
            />
            <EdroneToggle
              unique-id=""
              :checked="simei"
              :value="simei"
              label="SIMEI"
              :disabled="!simplesNacional"
              @click="simei = $event"
            />
          </div>
        </div>
      </template>
    </div>

    <div class="tw:flex-shrink-0">
      <EdroneNavigationBottom>
        <template #start>
          <EdroneButton type="text" @click="handleBack">
            {{ trans["Close"] }}
          </EdroneButton>
        </template>
        <template #end>
          <EdroneButton
            class="tw:flex-1"
            :disabled="!isFormValid || !!actor?.snapshot.value.hasTag('upgradingPlan')"
            :loading="!!actor?.snapshot.value.hasTag('upgradingPlan')"
            @click="handleSubmitForm"
          >
            {{ trans._freemium.assasForm.confirm }}
          </EdroneButton>
        </template>
      </EdroneNavigationBottom>
    </div>
  </div>
</template>

<script setup lang="ts">
import { inject, ref, computed, onUnmounted, onMounted } from "vue";
import { symbolMachineFreemium } from "vue-app/components/Freemium/Freemium.machine.ts";
import { amplitudeSendEvent } from "vue-app/utils/amplitude.ts";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import EdroneButton from "edrone-components/EdroneButton.vue";
import EdroneInput from "edrone-components/EdroneInput.vue";
import EdroneRadioButton from "edrone-components/EdroneRadioButton.vue";
import EdroneSelectCustom from "edrone-components/EdroneSelectCustom.vue";
import EdroneToggle from "edrone-components/EdroneToggle.vue";
import EdroneNavigationBottom from "edrone-components/EdroneNavigationBottom.vue";
import EdroneNavigationTop from "edrone-components/EdroneNavigationTop.vue";
import EdroneHeader from "edrone-components/EdroneHeader.vue";

const actor = inject(symbolMachineFreemium);

function handleCustomerTypeChange(type: CustomerType) {
  customerType.value = type;
  amplitudeSendEvent("Freemium_AssasForm_CustomerType_Changed", {
    customer_type: type,
  });
}

function handleBack() {
  amplitudeSendEvent("Freemium_AssasForm_Back_Clicked");
  actor?.send({ type: "CANCEL" });
}

function handleSubmitForm() {
  amplitudeSendEvent("Freemium_AssasForm_Submitted");
  submitForm();
}

enum CustomerType {
  CPF = "CPF",
  CNPJ = "CNPJ",
}

const customerType = ref(CustomerType.CNPJ);
const fullName = ref(`${window.EDRONE_CONTEXT.user.firstName || ""} ${window.EDRONE_CONTEXT.user.lastName || ""}`);
const cpf = ref("");
const cnpj = ref("");
const email = ref(window.EDRONE_CONTEXT.user.email || "");
const phonePrefix = ref("+55");
const phoneNumber = ref("");
const postalCode = ref("");
const companyName = ref("");
const state = ref("");
const street = ref("");
const streetNumber = ref("");
const complement = ref("");
const simplesNacional = ref(false);
const simei = ref(false);

const assasForm = computed(() => {
  return {
    customerType: customerType.value,
    name: fullName.value.trim(),
    cpf_cnpj: customerType.value === "CPF" ? cpf.value : cnpj.value,
    email: email.value,
    phone_prefix: phonePrefix.value,
    phone_number: phoneNumber.value,
    company: customerType.value === CustomerType.CNPJ ? companyName.value : "",
    postal_code: postalCode.value,
    street: street.value,
    street_number: streetNumber.value,
    province: state.value,
    complement: complement.value,
    simples_nacional: customerType.value === CustomerType.CNPJ ? simplesNacional.value : false,
    simei: customerType.value === CustomerType.CNPJ ? simei.value : false,
  };
});

const isFormValid = computed(() => {
  if (customerType.value === CustomerType.CPF) {
    return (
      fullName.value.trim() !== "" &&
      cpf.value.trim() !== "" &&
      email.value.trim() !== "" &&
      phonePrefix.value.trim() !== "" &&
      phoneNumber.value.trim() !== "" &&
      postalCode.value.trim() !== "" &&
      street.value.trim() !== "" &&
      streetNumber.value.trim() !== "" &&
      state.value.trim() !== ""
    );
  } else {
    return (
      fullName.value.trim() !== "" &&
      cnpj.value.trim() !== "" &&
      email.value.trim() !== "" &&
      phonePrefix.value.trim() !== "" &&
      phoneNumber.value.trim() !== "" &&
      postalCode.value.trim() !== "" &&
      street.value.trim() !== "" &&
      streetNumber.value.trim() !== "" &&
      state.value.trim() !== "" &&
      companyName.value.trim() !== ""
    );
  }
});

onMounted(() => {
  const storedForm = localStorage.getItem("freemiumUpgradeAssasForm");
  if (storedForm) {
    const parsedForm = JSON.parse(storedForm);
    customerType.value = parsedForm.customerType || CustomerType.CNPJ;
    fullName.value = parsedForm.name || fullName.value;
    cpf.value = parsedForm.customerType === CustomerType.CPF ? parsedForm.cpf_cnpj || "" : cpf.value;
    cnpj.value = parsedForm.customerType === CustomerType.CNPJ ? parsedForm.cpf_cnpj || "" : cnpj.value;
    email.value = parsedForm.email || email.value;
    phonePrefix.value = parsedForm.phone_prefix || phonePrefix.value;
    phoneNumber.value = parsedForm.phone_number || phoneNumber.value;
    postalCode.value = parsedForm.postal_code || postalCode.value;
    companyName.value = parsedForm.company || companyName.value;
    state.value = parsedForm.province || state.value;
    street.value = parsedForm.street || street.value;
    streetNumber.value = parsedForm.street_number || streetNumber.value;
    complement.value = parsedForm.complement || complement.value;
    simplesNacional.value = parsedForm.simples_nacional || simplesNacional.value;
    simei.value = parsedForm.simei || simei.value;
  }
});

onUnmounted(() => {
  localStorage.setItem("freemiumUpgradeAssasForm", JSON.stringify(assasForm.value));
});

const submitForm = () => {
  actor?.send({
    type: "SUBMIT_ASSAS_FORM",
    form: assasForm.value,
  });
  localStorage.removeItem("freemiumUpgradeAssasForm");
};
</script>
